import {useMemo} from 'react'

import {parseFilterKeywords} from './utils/parse-filter-keywords'
import {filterConversationItems, filterThreads, type ParsedCommentsFilterState} from './utils/filter-items'
import {buildConversationItems, type ConversationItem} from './utils/build-conversation-items'
import {useDiffPathOrderMap, useIssueComments, useReviewSummaries, useThreads} from '../../stores/layout-selectors'
import {sortConversationItemsByDate, sortThreadItemsByDiffOrder} from './utils/sort-items'
import {getConversationItemsAuthors, type AuthorFilterItem} from './utils/get-author-filters'
import {useIssueCommentLiveUpdates} from '../../hooks/use-issue-comment-live-updates'
import {CommentsSort, type CommentsFilterState} from './use-comments-filter-state'
import {isFeatureEnabled} from '@github-ui/feature-flags'

export interface UseFilteredCommentsDataOptions {
  /** Current filter state. */
  filterState: CommentsFilterState
}

export interface FilteredCommentsData {
  /** Unique authors of unfiltered items, for the author filter. */
  authorList: AuthorFilterItem[]
  /** Conversation items matching the conversation filter and sort direction. */
  filteredConversationItems: ConversationItem[]
  /** Total number of conversation items in this PR. */
  totalConversationItemsCount: number
}

const emptyItems = {
  unfilteredItems: [],
  filteredSortedItems: [],
}

/** `true` if we are only showing a flat list of review comments, based on the current filter settings. */
export function isReviewCommentsMode(filterState: ParsedCommentsFilterState) {
  return filterState.sort === CommentsSort.DiffOrder
}

export function useFilteredCommentsData({filterState}: UseFilteredCommentsDataOptions): FilteredCommentsData {
  const parsedFilterState = useMemo<ParsedCommentsFilterState>(
    () => ({
      ...filterState,
      parsedKeywords: parseFilterKeywords(filterState.filterText),
    }),
    [filterState],
  )

  const threadPreviews = useThreads()

  const diffPathOrderMap = useDiffPathOrderMap()

  const issueComments = useIssueComments()
  useIssueCommentLiveUpdates()

  const reviewSummaries = useReviewSummaries()

  // Diff-order sort displays threads as flat list without issue comments or review summaries
  const diffOrderItems = useMemo(() => {
    if (!isReviewCommentsMode(parsedFilterState)) return emptyItems

    const unfilteredItems = buildConversationItems([], [], threadPreviews)
    const filteredSortedItems = sortThreadItemsByDiffOrder(
      filterConversationItems(unfilteredItems, parsedFilterState),
      diffPathOrderMap,
    )

    return {unfilteredItems, filteredSortedItems}
  }, [parsedFilterState, threadPreviews, diffPathOrderMap])

  // Date-order sort displays full list of comments and reviews with threads
  const dateOrderItems = useMemo(() => {
    if (isReviewCommentsMode(parsedFilterState)) return emptyItems

    // Items are built using filtered threads so that we don't show non-matching threads inside matching review items
    const threadsForItems = isFeatureEnabled('prs_docked_comments_replies')
      ? filterThreads(threadPreviews, parsedFilterState)
      : []

    const unfilteredItems = buildConversationItems(issueComments, reviewSummaries, threadsForItems)
    const filteredSortedItems = sortConversationItemsByDate(
      filterConversationItems(unfilteredItems, parsedFilterState),
      parsedFilterState.sort as 'newest-first' | 'oldest-first',
    )

    return {unfilteredItems, filteredSortedItems}
  }, [parsedFilterState, issueComments, reviewSummaries, threadPreviews])

  // Even though we resolve to a single value, the two separate memos help minimize dependencies per-mode to reduce
  // unnecessary calculations and renders, at a small complexity cost
  const resolvedItems = isReviewCommentsMode(parsedFilterState) ? diffOrderItems : dateOrderItems

  const authorList = useMemo(
    () => getConversationItemsAuthors(resolvedItems.unfilteredItems),
    [resolvedItems.unfilteredItems],
  )

  return {
    filteredConversationItems: resolvedItems.filteredSortedItems,
    authorList,
    totalConversationItemsCount: resolvedItems.unfilteredItems?.length ?? 0,
  }
}
